/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { AviDropdownButtonItemComponent } from './avi-dropdown-button-item';
import { IAviDropdownButtonAction } from '../avi-dropdown-button.component';
import './avi-dropdown-button-list.component.less';

/**
 * @description
 *     Component for rendering a list of dropdown items. Uses FocusKeyManager to manage keydown and
 *     keyup events.
 * @author alextsg
 */
@Component({
    selector: 'avi-dropdown-button-list',
    templateUrl: './avi-dropdown-button-list.component.html',
})
export class AviDropdownButtonListComponent {
    /**
     * List of actions shown in the dropdown menu.
     */
    @Input()
    public actions: IAviDropdownButtonAction[] = [];

    @Input()
    public disabled = false;

    /**
     * Optional class name passed to each option element, ex. a 'sel-' class can be added.
     */
    @Input()
    public actionClassName?: string;

    /**
     * Passed to an action's onClick, disabled, and hidden functions as an additional param. Useful
     * when this component is dynamically/iteratively rendered and those properties are dependent
     * on additional factors. Ex. when this component is used to render singleactions in the
     * avi-data-grid, some actions may be hidden based on the row this component is part of.
     */
    @Input()
    public context?: any;

    @Output()
    public onHideDropdown = new EventEmitter<void>();

    /**
     * Reference to child AviDropdownButtonItem components, used by the FocusKeyManager for keyboard
     * controls.
     */
    @ViewChildren(AviDropdownButtonItemComponent)
    private aviDropdownButtonItems: QueryList<AviDropdownButtonItemComponent>;

    private keyManager: FocusKeyManager<AviDropdownButtonItemComponent>;

    /**
     * This is a property used to essentially negate the first keyup event seen by this component.
     * This is needed because avi-tooltip opens the dropdown menu with a keypress, so the keyup
     * event from that keypress will be seen by this popup. Without this, the first option will
     * immediately be selected after opening.
     *
     * Similarly, we need to use the keyup event to trigger action clicks. If we were to use keydown
     * or keypress, and the action opened a dialog, then the keyup would be seen by the dialog and
     * could trigger something on the dialog.
     */
    private initialKeyup = false;

    /**
     * Listen for keydown events. Used to close the dropdown on Escape/Tab keydowns.
     */
    @HostListener('keydown', ['$event'])
    private onKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Escape':
            case 'Tab':
                event.stopPropagation();
                event.preventDefault();
                this.onHideDropdown.emit();
                break;
        }
    }

    /**
     * Listen for keyup events. Used to navigate the dropdown menu and select actions.
     */
    @HostListener('keyup', ['$event'])
    private onKeyup(event: KeyboardEvent): void {
        // Further context provided above, but this is used to negate the first keyup event.
        if (!this.initialKeyup) {
            this.initialKeyup = true;

            return;
        }

        switch (event.code) {
            case 'ArrowDown':
            case 'ArrowUp':
                event.stopPropagation();
                event.preventDefault();
                this.keyManager.onKeydown(event);
                break;

            case 'Enter':
            case 'Space': {
                event.stopPropagation();
                event.preventDefault();

                const index = this.keyManager.activeItemIndex;

                this.clickAction(this.actions[index]);
                break;
            }
        }
    }

    /**
     * @override
     * Initiate FocusKeyManager and focus first item.
     */
    public ngAfterViewInit(): void {
        this.keyManager = new FocusKeyManager(this.aviDropdownButtonItems)
            .withVerticalOrientation()
            .withWrap()
            .skipPredicate(item => item.shouldSkip);

        setTimeout(() => this.keyManager.setFirstItemActive());
    }

    /**
     * @override
     * Clean up FocusKeyManager instance.
     */
    public ngOnDestroy(): void {
        this.keyManager.destroy();
    }

    /**
     * Trackby function used in *ngFor.
     */
    public trackByAction(
        index: number,
        action: IAviDropdownButtonAction,
    ): IAviDropdownButtonAction['label'] {
        return action.label;
    }

    /**
     * Check if action should be disabled.
     */
    public disableAction(action: IAviDropdownButtonAction): boolean {
        if (typeof action.disabled !== 'function') {
            return false;
        }

        try {
            return action.disabled(this.context);
        } catch (error) {
            throw new Error(`action.disabled failed: ${error}`);
        }
    }

    /**
     * Check if action should be hidden.
     */
    public hideAction(action: IAviDropdownButtonAction): boolean {
        if (typeof action.hidden !== 'function') {
            return false;
        }

        try {
            return action.hidden(this.context);
        } catch (error) {
            throw new Error(`action.hidden failed: ${error}`);
        }
    }

    /**
     * Select an action.
     */
    public clickAction(action: IAviDropdownButtonAction): void {
        if (!this.disabled && !this.disableAction(action)) {
            this.onHideDropdown.emit();

            action.onClick(this.context);
        }
    }
}
